import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { axiosInstance } from "../../../utils/networking";
import { BROADCAST_TYPES } from "./broadcastTypes";
import StatusChip from "./BroadcastStatusChip";

interface SummaryData {
  total: number;
  sent: number;
  delivered: number;
  pending: number;
  errors: number;
  notAttempted: number;
  skipped: number;
  deliveryFailures?: {
    bounced: number;
    dropped: number;
    blocked: number;
    spam_report: number;
  };
}

interface RecipientRow {
  _id: string;
  firm_id?: string;
  firm_name?: string;
  case_id?: string;
  contact?: string;
  emails?: string[];
  status?: string;
  delivery_status?: string;
  delivery_reason?: string;
  delivery_at?: string;
  chunkIndex?: number;
  skip_reason?: string;
}

const PAGE_SIZE = 50;

const FILTERS = [
  { id: "error", label: "Errors" },
  { id: "all", label: "All" },
  { id: "delivered", label: "Delivered" },
  { id: "pending", label: "Pending" },
  { id: "skipped", label: "Skipped" },
];

const DELIVERY_LABELS: Record<string, string> = {
  delivered: "Delivered",
  bounced: "Bounced",
  dropped: "Dropped",
  blocked: "Blocked",
  spam_report: "Spam report",
};

const describeRow = (row: RecipientRow) => {
  if (row.status === "error") return "Send failed";
  if (row.status === "not_attempted") return "Not attempted (send aborted)";
  if (row.status === "skipped") return "Skipped";
  if (row.delivery_status)
    return DELIVERY_LABELS[row.delivery_status] || row.delivery_status;
  return "Pending";
};

const isProblem = (row: RecipientRow) =>
  row.status === "error" ||
  row.status === "not_attempted" ||
  (!!row.delivery_status && row.delivery_status !== "delivered");

const Tile = ({
  label,
  value,
  error,
}: {
  label: string;
  value: number;
  error?: boolean;
}) => (
  <Box
    sx={{
      flex: "1 1 100px",
      minWidth: 100,
      border: "1px solid #e0e0e0",
      borderRadius: 1,
      px: 1.5,
      py: 1,
      backgroundColor: error ? "#fdecea" : "#fafafa",
    }}
  >
    <Typography
      variant="h6"
      fontWeight={700}
      color={error ? "error.main" : "text.primary"}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const BroadcastDetailDrawer = ({
  open,
  broadcast,
  onClose,
  onExited,
}: {
  open: boolean;
  broadcast: any | null;
  onClose: () => void;
  onExited?: () => void;
}) => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [total, setTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState("");
  const [filter, setFilter] = useState("error");
  const [page, setPage] = useState(0);

  const broadcastId = broadcast?._id;

  const fetchSummary = async () => {
    if (!broadcastId) return;
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const res = await axiosInstance.get(`/broadcast/${broadcastId}/summary`);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error("Failed to fetch broadcast summary:", err);
      setSummaryError("Could not load the delivery summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchRecipients = async () => {
    if (!broadcastId) return;
    setRowsLoading(true);
    setRowsError("");
    try {
      const res = await axiosInstance.get(
        `/broadcast/${broadcastId}/recipients`,
        { params: { filter, page, pageSize: PAGE_SIZE } },
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch broadcast recipients:", err);
      setRows([]);
      setTotal(0);
      setRowsError("Could not load recipients.");
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !broadcastId) return;
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, broadcastId]);

  useEffect(() => {
    if (!open || !broadcastId) return;
    fetchRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, broadcastId, filter, page]);

  const handleRefresh = () => {
    fetchSummary();
    fetchRecipients();
  };

  const handleFilter = (id: string) => {
    setPage(0);
    setFilter(id);
  };

  const noTracking = !!summary && summary.total === 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{ onExited }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              {broadcast?.subject || "Broadcast"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {BROADCAST_TYPES.find((t) => t.id === broadcast?.broadcastType)
                ?.label || broadcast?.broadcastType}
              {broadcast?.fromBFC ? ` · ${broadcast.fromBFC}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {broadcast?.createdAt
                ? dayjs(broadcast.createdAt).format("MMM D, YYYY h:mm A")
                : "—"}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {broadcast?.status && <StatusChip status={broadcast.status} />}
            </Box>
          </Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        {summaryLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!summaryLoading && summaryError && (
          <Typography variant="body2" color="error">
            {summaryError}
          </Typography>
        )}

        {!summaryLoading && !summaryError && summary && (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Tile label="Recipients" value={summary.total} />
              <Tile label="Delivered" value={summary.delivered} />
              <Tile label="Pending confirmation" value={summary.pending} />
              <Tile
                label="Errors"
                value={summary.errors}
                error={summary.errors > 0}
              />
              <Tile label="Skipped" value={summary.skipped} />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Delivery confirmations can take a few minutes to arrive.
            </Typography>
          </>
        )}
      </Box>

      {noTracking ? (
        <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            No per-recipient delivery data for this broadcast.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {FILTERS.map((f) => (
              <Chip
                key={f.id}
                label={f.label}
                size="small"
                onClick={() => handleFilter(f.id)}
                variant={filter === f.id ? "filled" : "outlined"}
                sx={
                  filter === f.id
                    ? { backgroundColor: "#9d2362", color: "#fff" }
                    : undefined
                }
              />
            ))}
          </Box>

          {rowsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : rowsError ? (
            <Typography variant="body2" color="error">
              {rowsError}
            </Typography>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No recipients match this filter.
              </Typography>
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Firm</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const reason = row.delivery_reason || row.skip_reason;
                    return (
                      <TableRow key={row._id}>
                        <TableCell>
                          {row.case_id ? (
                            <Link
                              href={`/case?id=${row.case_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ color: "#9d2362" }}
                            >
                              {row.firm_name || row.firm_id}
                            </Link>
                          ) : (
                            row.firm_name || row.firm_id || "—"
                          )}
                        </TableCell>
                        <TableCell>{row.contact || "—"}</TableCell>
                        <TableCell sx={{ wordBreak: "break-all" }}>
                          {row.emails?.[0] || "—"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            <Typography
                              variant="body2"
                              color={isProblem(row) ? "error.main" : "text.primary"}
                            >
                              {describeRow(row)}
                            </Typography>
                            {reason && (
                              <Tooltip title={reason}>
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 15, color: "text.secondary" }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.delivery_at
                            ? dayjs(row.delivery_at).format("MMM D, h:mm A")
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default BroadcastDetailDrawer;
